import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	Button,
	Card,
	Input,
	Select,
	Space,
	Table,
	Typography,
	message,
} from "antd";
import PageHeader from "../common/PageHeader";

const { Text } = Typography;

const ApprovalRouteAssignments = () => {
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [routes, setRoutes] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [defaultRouteId, setDefaultRouteId] = useState(null);
	const [searchText, setSearchText] = useState("");
	const [dirty, setDirty] = useState({});

	const load = useCallback(async () => {
		try {
			setLoading(true);
			const res = await useAxios.get(
				"/utils/leave/approval-route-assignments/",
			);
			setRoutes(res.data.routes || []);
			setEmployees(res.data.employees || []);
			setDefaultRouteId(res.data.default_route_id);
			setDirty({});
		} catch {
			message.error("적용 인원 목록을 불러오지 못했습니다.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const filtered = useMemo(() => {
		const keyword = searchText.trim().toLowerCase();
		if (!keyword) return employees;
		return employees.filter((row) =>
			String(row.name || "")
				.toLowerCase()
				.includes(keyword),
		);
	}, [employees, searchText]);

	const setRoute = (userId, routeId) => {
		setEmployees((prev) =>
			prev.map((row) =>
				row.user_id === userId
					? {
							...row,
							route_id: routeId,
							route_name:
								routes.find((r) => r.id === routeId)?.name ||
								row.route_name,
							is_default: routeId === defaultRouteId,
						}
					: row,
			),
		);
		setDirty((prev) => ({ ...prev, [userId]: routeId }));
	};

	const save = async () => {
		const assignments = Object.entries(dirty).map(([userId, routeId]) => ({
			user_id: Number(userId),
			route_id: routeId,
		}));
		if (!assignments.length) {
			message.info("변경된 항목이 없습니다.");
			return;
		}
		try {
			setSaving(true);
			await useAxios.put("/utils/leave/approval-route-assignments/", {
				assignments,
			});
			message.success("적용 인원을 저장했습니다.");
			await load();
		} catch (error) {
			message.error(
				error?.response?.data?.error || "저장에 실패했습니다.",
			);
		} finally {
			setSaving(false);
		}
	};

	const columns = [
		{ title: "이름", dataIndex: "name", width: 120 },
		{ title: "직분", dataIndex: "job", width: 100 },
		{
			title: "구분",
			dataIndex: "classification_name",
			width: 100,
		},
		{
			title: "결재 순서",
			dataIndex: "route_id",
			render: (value, row) => (
				<Select
					style={{ width: 240 }}
					value={value}
					options={routes.map((r) => ({
						value: r.id,
						label: r.name,
					}))}
					onChange={(next) => setRoute(row.user_id, next)}
				/>
			),
		},
	];

	return (
		<>
			<PageTitle title="결재 순서 적용 인원" />
			<div className="page">
				<PageHeader
					title="결재 순서 적용 인원"
				/>
				<Card className="surface-card">
					<Space style={{ marginBottom: 16 }} wrap>
						<Input.Search
							allowClear
							placeholder="이름 검색"
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							style={{ width: 240 }}
						/>
						<Button type="primary" loading={saving} onClick={save}>
							변경 저장 ({Object.keys(dirty).length})
						</Button>
						<Text type="secondary">
							미지정 인원은 기본값(
							{routes.find((r) => r.id === defaultRouteId)?.name ||
								"일반 교직원"}
							)이 적용됩니다.
						</Text>
					</Space>
					<Table
						rowKey="user_id"
						loading={loading}
						columns={columns}
						dataSource={filtered}
						pagination={{ pageSize: 30, showSizeChanger: true }}
						sticky={{ offsetHeader: 64 }}
					/>
				</Card>
			</div>
		</>
	);
};

export default ApprovalRouteAssignments;
